import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useFormContext, Controller } from "react-hook-form";

// Import country-state-city
// TODO: Look  for a more optimized method
import { Country, City } from "country-state-city";

// Import inhouse icons
import { X } from "lucide-react";

// Import reusable components
import FormSelect from "@/components/formInputFields/FormSelect";
import FormInput from "@/components/formInputFields/FormInput";
import FormCard from "@/components/formInputFields/FormCard";
import FormPhoneInput from "@/components/formInputFields/FormPhoneInput";

export default function CustomerAddressModal({ onClose, countryOptions = [] }) {
  const [portalNode, setPortalNode] = useState(null);
  const [selectedCountryIso, setSelectedCountryIso] = useState("KE");

  const {
    register,
    control,
    formState: { errors },
  } = useFormContext();

  const defaultAddressErrors = errors?.defaultAddress ?? {};

  // Import the countries and cities from the library
  const countries = Country.getAllCountries();
  const cities = City.getCitiesOfCountry(selectedCountryIso);

  useEffect(() => {
    const node = document.createElement("div");
    node.setAttribute("data-customer-address-modal-root", "true");
    document.body.appendChild(node);
    setPortalNode(node);

    const handleEsc = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    document.addEventListener("keydown", handleEsc);

    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.removeChild(node);
      setPortalNode(null);
    };
  }, [onClose]);

  if (!portalNode) return null;

  const handleDialogClick = (event) => {
    event.stopPropagation();
  };

  const handleSave = () => {
    onClose?.();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[50] flex items-center justify-center bg-[#000]/45 px-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex w-[600px] max-h-[95%] flex-col overflow-hidden rounded-lg bg-white"
        onClick={handleDialogClick}
      >
        {/* Header */}
        <div className="flex items-center justify-between rounded-t-lg bg-[#ececec] px-6 py-4">
          <span className="font-medium text-[#000]">Primary Address</span>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 transition hover:bg-[#f3f4f6]"
            aria-label="Close modal"
          >
            <X className="h-5 w-5 text-[#333333]" />
          </button>
        </div>

        <div className=" overflow-y-auto p-6 text-sm text-[#333]">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Country */}
            <FormSelect
              label="Country"
              name="defaultAddress.country"
              options={countries.map(c => ({
                value: c.isoCode,
                label: c.name
              }))}
              register={register}
              error={defaultAddressErrors?.country}
              onChange={(e) => {
                register("defaultAddress.country").onChange(e);
                setSelectedCountryIso(e.target.value);
              }}
            />

            {/* City */}
            <FormSelect
              label="City"
              name="defaultAddress.city"
              options={City.getCitiesOfCountry(selectedCountryIso)?.map(c => ({
                value: c.name,
                label: c.name
              })) || []}
              register={register}
              error={defaultAddressErrors?.city}
            />
          </div>

          {/* Company */}
          <FormInput
            label="Company"
            name="defaultAddress.company"
            placeholder="Company"
            register={register}
            error={defaultAddressErrors?.company}
          />
            
          {/* Address */}
          <FormInput
            label="Address"
            name="defaultAddress.addressLine1"
            placeholder="Address"
            register={register}
            error={defaultAddressErrors?.addressLine1}
          />

          {/* Apartment, suite, etc */}
          <FormInput
            label="Apartment, suite, etc"
            name="defaultAddress.addressLine2"
            placeholder="Apartment, suite, etc"
            register={register}
            error={defaultAddressErrors?.addressLine2}
          />

          <div className="grid gap-4 md:grid-cols-2">
            {/* Postal code */}
            <FormInput
              label="Postal code"
              name="defaultAddress.postalCode"
              placeholder="Postal code"
              register={register}
              error={defaultAddressErrors?.postalCode}
              className="mb-0"
            />
          </div>

          {/* Phone */}
          <Controller
            name="defaultAddress.phone"
            control={control}
            rules={{ required: "Phone number is required" }}
            render={({ field }) => (
              <FormPhoneInput
                label="Phone"
                {...field}
                error={defaultAddressErrors?.phone}
              />
            )}
          />
        </div>

        <div className="flex justify-end gap-2 py-3 px-5 border-t border-[#d2d2d2]">
          <button
            onClick={() =>{}}
            className="px-4 py-2 text-sm font-medium text-[#333333] bg-white border border-[#9e9e9e] rounded-[10px] hover:bg-[#f3f3f3]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-6 py-2 text-sm font-medium text-white rounded-[10px] transition bg-[#333333] hover:bg-[#333333db]"
          >
            Save
          </button>
        </div>
      </div>
    </div>,
    portalNode
  );
}
